import {useTranslation} from 'react-i18next';
import moment from 'moment/moment';
import {getAdmissionStartDate} from '../../../../util/patientUtil';
import React, {Fragment, useEffect, useState} from 'react';
import {SEPSIS_TOOL_TYPE, sepsisToolAgeParameter} from '../../forms/formsUtil';
import _ from 'lodash';
import {mapObservationFromAPI} from '../../forms/observation/obsUtils';
import Paper from '@mui/material/Paper';
import {FormControl, FormControlLabel, Grid, RadioGroup} from '@mui/material';
import Radio from '@mui/material/Radio';
import InputAdornment from '@mui/material/InputAdornment';
import {useSepsisStyles} from '../sepsisToolForm';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';

const PaedsSepsisTool = (props) => {
    const classes = useSepsisStyles();
    const {t} = useTranslation();

    const age = moment.duration(getAdmissionStartDate(props.patient?.admission).diff(moment(props.patient?.patient?.dob))).asYears();

    const [form, setForm] = useState({
        temperature: null,
        temperatureValue: '',
        tachycardiaValue: age,
        heartRate: null,
        heartRateValue: '',
        respRate: null,
        respRateValue: '',
        mentalState: null,
        skin: null,
        clinicalConcern: null,
        source: null,
        sourceText: '',
        status: SEPSIS_TOOL_TYPE.NEW,
    });

    const [isReadOnly, setIsReadOnly] = useState(false);

    useEffect(() => {
        let cancel = false;
        if (_.isEmpty(props.data) || props.data.status === SEPSIS_TOOL_TYPE.NEW || props.data.status === SEPSIS_TOOL_TYPE.IN_COMPLETED) {
            if (props.patient?.patient?.lastObservation) {
                checkObs(props.patient?.patient?.lastObservation);
            }
        }

        // Get selected sepsis
        if (!_.isEmpty(props.data)) {
            setIsReadOnly(true);
            setForm(props.data);
        }

        return () => {
            cancel = true;
        };
    }, []);

    useEffect(() => {
        if (form !== props.data) {
            props.buttonShow(checkTwoPlusOne());

            props.save(createObject());
        }
    }, [form]);

    const createObject = () => {
        return {
            type: 'paeds',
            startAge: age,
            ...form,
        };
    };

    const checkObs = (obs) => {
        if (form.status === SEPSIS_TOOL_TYPE.NEW && _.isEmpty(props.data)) {
            if (obs && obs.tests) {
                const mappedObs = mapObservationFromAPI(obs, true);
                let respRate = mappedObs.respiration;
                let pulseRate = mappedObs.pulseRate;
                let temperature = mappedObs.temperature;

                // Check test status, if not completed then set default value
                respRate = respRate?.status === 'completed' ? respRate?.breaths : null;
                pulseRate = pulseRate?.status === 'completed' ? pulseRate?.pulseRate : null;
                temperature = temperature?.status === 'completed' ? temperature?.temperature : null;

                const respValue = sepsisToolAgeParameter(age, 'RR');
                const pulseValue = sepsisToolAgeParameter(age, 'HR');

                const dataSet = {
                    temperature: !temperature ? null : (temperature < 36 || temperature > 38.5) ? 'yes' : 'no',
                    temperatureValue: temperature ? temperature.toString() : '',
                    heartRateValue: pulseRate ? pulseRate.toString() : '',
                    heartRate: !pulseRate ? null : pulseRate > pulseValue ? 'yes' : 'no',
                    respRateValue: respRate ? respRate.toString() : '',
                    respRate: !respRate ? null : respRate > respValue ? 'yes' : 'no',
                };

                setForm(prevState => ({
                    ...prevState,
                    ...dataSet,
                    obsUsed: obs.examinationStart,
                }));
            } else {
                setForm(prevState => ({
                    ...prevState,
                    obsUsed: null,
                }));
            }
        }
    };

    const checkTwoPlusOne = () => {
        let section1 = (form.temperature === 'yes' ? 1 : 0) + (form.tachycardia === 'yes' ? 1 : 0) + (form.heartRate === 'yes' ? 1 : 0) + (form.respRate === 'yes' ? 1 : 0);
        let section2 = form.mentalState === 'yes' || form.skin === 'yes' || form.clinicalConcern === 'yes' || form.source === 'confirmed';

        return section1 >= 2 && section2;
    };

    const renderInfo = () => {
        if (checkTwoPlusOne()) {
            return (
                <div className={classes.buttonSave} style={{textAlign: 'center'}}>
                    {t('2+1 criteria present - THINK SEPSIS: This is an emergency')} <br/>
                    {t('Immediate ST4+ (or equivalent) review and follow Sepsis 6')} <br/>
                    {t('If senior decision not to proceed to sepsis 6 immediately, state reason')}<br/>
                    {t('If diagnosis unclear / sepsis not excluded, consider bloods (VBG/FBC/UE/CRP/BC/lact) & repeat senior review')}
                </div>
            );
        } else {
            return (
                <div className={classes.buttonSave} style={{textAlign: 'center'}}>
                    {t('SEPSIS UNLIKELY: Document your clinical impression in reason for SEPSIS not required')}
                </div>
            );
        }
    };

    const changeValueHandler = (event) => {
        const {name, value} = event.target;

        setForm(prevState => ({...prevState, [name]: value}));
    };

    return (
        <>
            <div className={classes.section} style={{width: '100%'}}>
                <Paper elevation={3} className={classes.category}>
                    {t('Could this child have an infection? Could it be sepsis? Look for 2 of:')}
                </Paper>

                <div>
                    <Grid item xs={12} style={{marginBottom: 5}}>
                        <Grid container spacing={2} direction="row">
                            <Grid item xs={12} md={7}>
                                <div className={classes.sectionBox}>
                                  <span className={classes.smallFont}>
                                      <b>{t('Temperature under 36°C or over 38.5°C')} </b>
                                        <span style={{color: '#dd7e6b', fontSize: 11}}>
                                            {t(' (NB >38°C for Oncology patients or <3/12)')}
                                        </span>
                                  </span>
                                </div>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <div className={classes.sectionBox} style={{textAlign: 'center'}}>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            value={form.temperature}
                                            name="temperature"
                                            onChange={changeValueHandler}
                                            row
                                        >
                                            <FormControlLabel
                                                disabled
                                                value="yes"
                                                control={<Radio size="small"/>}
                                                label={t('YES')}
                                            />
                                            <FormControlLabel
                                                disabled
                                                value="no"
                                                control={<Radio size="small"/>}
                                                label={t('NO')}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </div>
                            </Grid>

                            <Grid item xs={12} md={2}>
                                <AgyleTextField
                                    value={form.temperatureValue}
                                    onChange={() => null}
                                    endAdornment={<InputAdornment position="end">°C</InputAdornment>}
                                    fullWidth
                                    noBorder={false}
                                    withNewBackground
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} style={{marginBottom: 5}}>
                        <Grid container spacing={2} direction="row">
                            <Grid item xs={12} md={10}>
                                <div className={classes.sectionBox}>
                                    <span
                                        className={classes.smallFont}
                                        style={{fontWeight: 'bold'}}
                                    >
                                        {t('Tachycardia (')}&#8593; {t('HR). Tachypnoea (')}&#x2191; {t('RR)')}
                                    </span>
                                </div>
                            </Grid>

                            <Grid item xs={12} md={2}>
                                <AgyleTextField
                                    value={form.tachycardiaValue}
                                    onChange={() => null}
                                    endAdornment={<InputAdornment position="end">Age</InputAdornment>}
                                    fullWidth
                                    noBorder={false}
                                    withNewBackground
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} style={{marginBottom: 5}}>
                        <Grid container spacing={2} direction="row">
                            <Grid item xs={12} md={7}>
                                <div className={classes.sectionBox}>
                                    <span
                                        className={classes.smallFont}
                                        style={{fontWeight: 'bold'}}
                                    >
                                        {t('HR over ')} {sepsisToolAgeParameter(age, 'HR')}
                                    </span>
                                </div>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <div className={classes.sectionBox} style={{textAlign: 'center'}}>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            value={form.heartRate}
                                            name="heartRate"
                                            onChange={changeValueHandler}
                                            row
                                        >
                                            <FormControlLabel
                                                disabled
                                                value="yes"
                                                control={<Radio size="small"/>}
                                                label={t('YES')}
                                            />
                                            <FormControlLabel
                                                disabled
                                                value="no"
                                                control={<Radio size="small"/>}
                                                label={t('NO')}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </div>
                            </Grid>

                            <Grid item xs={12} md={2}>
                                <AgyleTextField
                                    value={form.heartRateValue}
                                    onChange={() => null}
                                    endAdornment={<InputAdornment position="end">/ min</InputAdornment>}
                                    fullWidth
                                    noBorder={false}
                                    withNewBackground
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} style={{marginBottom: 5}}>
                        <Grid container spacing={2} direction={'row'}>
                            <Grid item xs={12} md={7}>
                                <div className={classes.sectionBox}>
                                    <span
                                        className={classes.smallFont}
                                        style={{fontWeight: 'bold'}}
                                    >
                                        {t('RR over ')} {sepsisToolAgeParameter(age, 'RR')}
                                    </span>
                                </div>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <div className={classes.sectionBox} style={{textAlign: 'center'}}>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            value={form.respRate}
                                            name="respRate"
                                            onChange={changeValueHandler}
                                            row
                                        >
                                            <FormControlLabel
                                                disabled
                                                value="yes"
                                                control={<Radio size="small"/>}
                                                label={t('YES')}
                                            />
                                            <FormControlLabel
                                                disabled
                                                value="no"
                                                control={<Radio size="small"/>}
                                                label={t('NO')}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </div>
                            </Grid>

                            <Grid item xs={12} md={2}>
                                <AgyleTextField
                                    value={form.respRateValue}
                                    onChange={() => null}
                                    endAdornment={<InputAdornment position="end">/ min</InputAdornment>}
                                    fullWidth
                                    noBorder={false}
                                    withNewBackground
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </div>
            </div>

            <div className={classes.section} style={{width: '100%'}}>
                <Paper elevation={3} className={classes.category}>
                    {t('Plus 1 of:')}
                </Paper>

                <div>
                    <Grid item xs={12} style={{marginBottom: 5}}>
                        <Grid container spacing={2} direction="row">
                            <Grid item xs={12} md={9}>
                                <div className={classes.sectionBox}>
                                    <span className={classes.smallFont}>
                                        <b>{t('Altered mental state:')}</b>{t(' Sleepy, floppy, lethargic, irritable')}
                                    </span>
                                </div>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <div className={classes.sectionBox} style={{textAlign: 'center'}}>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            value={form.mentalState}
                                            name="mentalState"
                                            onChange={changeValueHandler}
                                            row
                                        >
                                            <FormControlLabel
                                                disabled={isReadOnly}
                                                value="yes"
                                                control={<Radio size="small"/>}
                                                label={t('YES')}
                                            />
                                            <FormControlLabel
                                                disabled={isReadOnly}
                                                value="no"
                                                control={<Radio size="small"/>}
                                                label={t('NO')}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </div>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} style={{marginBottom: 5}}>
                        <Grid container spacing={2} direction="row">
                            <Grid item xs={12} md={9}>
                                <div className={classes.sectionBox}>
                                    <span className={classes.smallFont} style={{fontWeight: 'bold'}}>
                                        {t('Mottled skin OR prolonged capillary refill time OR ‘flash’ capillary refill time OR limb pain')}
                                    </span>
                                </div>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <div className={classes.sectionBox} style={{textAlign: 'center'}}>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            value={form.skin}
                                            name="skin"
                                            onChange={changeValueHandler}
                                            row
                                        >
                                            <FormControlLabel
                                                disabled={isReadOnly}
                                                value="yes"
                                                control={<Radio size="small"/>}
                                                label={t('YES')}
                                            />
                                            <FormControlLabel
                                                disabled={isReadOnly}
                                                value="no"
                                                control={<Radio size="small"/>}
                                                label={t('NO')}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </div>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} style={{marginBottom: 5}}>
                        <Grid container spacing={2} direction="row">
                            <Grid item xs={12} md={9}>
                                <div className={classes.sectionBox}>
                                    <span className={classes.smallFont}>
                                        <b>{t('Clinical concern')}</b>

                                        {t(' regarding possible sepsis')}

                                        <span style={{color: '#dd7e6b', fontSize: 12}}>
                                            {t(' – seek review if significant concern even if trigger criteria not met.')}
                                        </span>
                                    </span>
                                </div>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <div className={classes.sectionBox} style={{textAlign: 'center'}}>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            value={form.clinicalConcern}
                                            name="clinicalConcern"
                                            onChange={changeValueHandler}
                                            row
                                        >
                                            <FormControlLabel
                                                disabled={isReadOnly}
                                                value="yes"
                                                control={<Radio size="small"/>}
                                                label={t('YES')}
                                            />

                                            <FormControlLabel
                                                disabled={isReadOnly}
                                                value="no"
                                                control={<Radio size="small"/>}
                                                label={t('NO')}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </div>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} style={{marginBottom: 5}}>
                        <Grid container spacing={2} direction="row">
                            <Grid item xs={12} md={9}>
                                <AgyleTextField
                                    name="sourceText"
                                    label={t('Site / Source')}
                                    variant="outlined"
                                    value={form.sourceText}
                                    onChange={changeValueHandler}
                                    readOnly={isReadOnly}
                                    fullWidth
                                    noBorder={false}
                                    withNewBackground
                                />

                                <div className={classes.sectionSource} style={{width: '100%'}}>
                                    {t('(BEWARE : The following are at particular RISK : Neonate / Immunocompromised / Recent Burn / recent VZV)')}
                                </div>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <div className={classes.sectionBox} style={{height: 76, paddingLeft: 32}}>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            value={form.source}
                                            name="source"
                                            onChange={changeValueHandler}
                                            row
                                        >
                                            <FormControlLabel
                                                disabled={isReadOnly}
                                                value="confirmed"
                                                control={<Radio size="small"/>}
                                                label={t('Confirmed')}
                                            />

                                            <FormControlLabel
                                                disabled={isReadOnly}
                                                value="suspected"
                                                control={<Radio size="small"/>}
                                                label={t('Suspected')}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </div>
                            </Grid>
                        </Grid>
                    </Grid>
                </div>
            </div>

            {renderInfo()}
        </>
    );
};

export default PaedsSepsisTool;